import { z } from 'zod';
import { CharSetForPhoneAndFax, SftpProtocol, IDE_THRESHOLD_MAX, IDE_THRESHOLD_MIN } from '../../constants';
import {
    FTP_PASSWORD_RULES,
    getMaxTrialEndDate,
    getMinTrialEndDate,
    MAX_TRIAL_LENGTH_IN_DAYS,
    validatePasswordFTP,
} from './BuyerSignUp.utils';

const GeneralInformationSchema = z.object({
    buyerCompanyName: z
        .string({ required_error: 'Company name is required' })
        .trim()
        .min(1, 'Company name is required'),
    buyerCompanyAddress: z.object({
        addressLine1: z
            .string({ required_error: 'Address Line 1 is required' })
            .trim()
            .min(1, 'Address Line 1 is required'),
        addressLine2: z.string().trim().optional(),
        addressLine3: z.string().trim().optional(),
        city: z.string({ required_error: 'City is required' }).trim().min(1, 'City is required'),
        state: z
            .string({
                required_error: 'State/Province/Territory is required',
                invalid_type_error: 'State/Province/Territory is required',
            })
            .trim()
            .min(1, 'State/Province/Territory is required'),
        zipCode: z.string({ required_error: 'Zip Code is required' }).trim().min(1, 'Zip Code is required'),
        country: z
            .string({ required_error: 'Country is required', invalid_type_error: 'Country is required' })
            .trim()
            .min(1, 'Country is required')
            .default('USA'),
        phone: z
            .string({ required_error: 'Phone is required' })
            .trim()
            .min(1, 'Phone is required')
            .regex(CharSetForPhoneAndFax, 'Invalid phone number'),
    }),
    contactSales: z.object({
        name: z.string({ required_error: 'Name is required' }).trim().min(1, 'Name is required'),
        phone: z
            .string({ required_error: 'Phone is required' })
            .trim()
            .min(1, 'Phone is required')
            .regex(CharSetForPhoneAndFax, 'Invalid phone number'),
        email: z
            .string({ required_error: 'Email is required' })
            .trim()
            .min(1, 'Email is required')
            .email('Invalid email address'),
    }),
    contactAdministrator: z.object({
        isSameAsSalesContact: z.boolean().default(false),
        name: z.string({ required_error: 'Name is required' }).trim().min(1, 'Name is required'),
        phone: z
            .string({ required_error: 'Phone is required' })
            .trim()
            .min(1, 'Phone is required')
            .regex(CharSetForPhoneAndFax, 'Invalid phone number'),
        email: z
            .string({ required_error: 'Email is required' })
            .min(1, 'Email is required')
            .email('Invalid email address'),
    }),
    erpSystemId: z.number({ required_error: 'ERP selection is required' }),
    maxSuppliers: z.string().trim().pipe(z.coerce.number().int().positive()).optional().or(z.literal('')),
    maxInvoices: z.string().trim().pipe(z.coerce.number().int().positive()).optional().or(z.literal('')),
    currencyType: z
        .number({ required_error: 'Currency type is required', invalid_type_error: 'Currency type is required' })
        .int(),
    trialEndDate: z
        .date({ required_error: 'Trial end date required', invalid_type_error: 'Trial end date required' })
        .min(getMinTrialEndDate(), 'Trial end date must be at least 1 day into the future')
        .max(getMaxTrialEndDate(true), `Trial cannot exceed ${MAX_TRIAL_LENGTH_IN_DAYS} days`)
        .default(getMaxTrialEndDate()),
});

const InboundDataFeedsSchema = z.object({
    deliveryMethod: z
        .object({
            useTransceptaSftp: z.boolean().default(true),
            useBuyerSftp: z.boolean().default(false),
            useEmail: z.boolean().default(false),
            // allow empty fields if sftpConnection not explicitly required
            sftpConnection: z
                .object({
                    server: z.string().trim().min(1, 'Server is required').optional().or(z.literal('')),
                    user: z.string().trim().min(1, 'User is required').optional().or(z.literal('')),
                    password: z.string().trim().min(1, 'Password is required').optional().or(z.literal('')),
                    downloadFolder: z
                        .string()
                        .trim()
                        .min(1, 'Download folder is required')
                        .optional()
                        .or(z.literal('')),
                    uploadFolder: z.string().trim().min(1, 'Upload folder is required').optional().or(z.literal('')),
                    protocol: z.nativeEnum(SftpProtocol).optional().default(SftpProtocol.SFTP),
                    port: z.string().trim().min(1, 'Port is required').optional().or(z.literal('')),
                    timeoutInSeconds: z
                        .string()
                        .trim()
                        .pipe(z.coerce.number().int().positive())
                        .optional()
                        .or(z.literal('')),
                })
                .optional(),
        })
        .superRefine(({ useTransceptaSftp, useBuyerSftp, sftpConnection, useEmail }, ctx) => {
            if (!useTransceptaSftp && !useBuyerSftp && !useEmail) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'At least one delivery method is required',
                });
            }
            // Transcepta or Buyer SFTP requires connection details
            if ((useTransceptaSftp || useBuyerSftp) && !useEmail) {
                if (!sftpConnection?.server) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Server is required',
                        path: ['sftpConnection', 'server'],
                    });
                }
                if (!sftpConnection?.user) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'User is required',
                        path: ['sftpConnection', 'user'],
                    });
                }
                if (!sftpConnection?.password) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Password is required',
                        path: ['sftpConnection', 'password'],
                    });
                }
                if (!sftpConnection?.downloadFolder) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Download folder is required',
                        path: ['sftpConnection', 'downloadFolder'],
                    });
                }
                if (!sftpConnection?.uploadFolder) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Upload folder is required',
                        path: ['sftpConnection', 'uploadFolder'],
                    });
                }
                if (!sftpConnection?.protocol) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Protocol is required',
                        path: ['sftpConnection', 'protocol'],
                    });
                }
                if (!sftpConnection?.port) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Port is required',
                        path: ['sftpConnection', 'port'],
                    });
                }
                if (!sftpConnection?.timeoutInSeconds) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Timeout in seconds is required',
                        path: ['sftpConnection', 'timeoutInSeconds'],
                    });
                }
            }
            // Transcepta SFTP has specific password requirements
            if (useTransceptaSftp && sftpConnection?.password) {
                if (!validatePasswordFTP(sftpConnection?.password)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: FTP_PASSWORD_RULES,
                        path: ['sftpConnection', 'password'],
                    });
                }
            }
        }),
    infinxIntegration: z
        .object({
            useInfinxIntegration: z.boolean().default(false),
            useExistingInfinxConnection: z.boolean().default(false),
            // allow empty fields if sftpConnection not explicitly required
            invoiceProfileId: z.string().trim().optional().or(z.literal('')),
            sftpConnection: z
                .object({
                    server: z.string().trim().min(1, 'Server is required').optional().or(z.literal('')),
                    user: z.string().trim().min(1, 'User is required').optional().or(z.literal('')),
                    password: z.string().trim().min(1, 'Password is required').optional().or(z.literal('')),
                    downloadFolder: z
                        .string()
                        .trim()
                        .min(1, 'Download folder is required')
                        .optional()
                        .or(z.literal('')),
                    uploadFolder: z.string().trim().min(1, 'Upload folder is required').optional().or(z.literal('')),
                    protocol: z.nativeEnum(SftpProtocol).optional().default(SftpProtocol.SFTP),
                    port: z.string().trim().min(1, 'Port is required').optional().or(z.literal('')),
                    timeoutInSeconds: z
                        .string()
                        .trim()
                        .pipe(z.coerce.number().int().positive())
                        .optional()
                        .or(z.literal('')),
                })
                .optional(),
        })
        .superRefine(({ useInfinxIntegration, useExistingInfinxConnection, sftpConnection, invoiceProfileId }, ctx) => {
            // infinx integration requires user and password
            if (useInfinxIntegration) {
                if (!sftpConnection?.user) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'User is required',
                        path: ['sftpConnection', 'user'],
                    });
                }
                if (!sftpConnection?.password) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Password is required',
                        path: ['sftpConnection', 'password'],
                    });
                }
                if (useExistingInfinxConnection && !invoiceProfileId) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Invoice Profile ID is required',
                        path: ['', 'invoiceProfileId'],
                    });
                }
                if (!sftpConnection?.server) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Server is required',
                        path: ['sftpConnection', 'server'],
                    });
                }
                if (!sftpConnection?.downloadFolder) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Download folder is required',
                        path: ['sftpConnection', 'downloadFolder'],
                    });
                }
                if (!sftpConnection?.uploadFolder) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Upload folder is required',
                        path: ['sftpConnection', 'uploadFolder'],
                    });
                }
                if (!sftpConnection?.protocol) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Protocol is required',
                        path: ['sftpConnection', 'protocol'],
                    });
                }
                if (!sftpConnection?.port) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Port is required',
                        path: ['sftpConnection', 'port'],
                    });
                }
                if (!sftpConnection?.timeoutInSeconds) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Timeout in seconds is required',
                        path: ['sftpConnection', 'timeoutInSeconds'],
                    });
                }
            }
        }),
    // for message recognition
    vendorFeedFormat: z
        .string({
            required_error: 'Vendor feed format is required',
            invalid_type_error: 'Vendor feed format is required',
        })
        .trim()
        .min(1, 'Vendor feed format is required'),
    userFeedFormat: z
        .string({ required_error: 'User feed format is required', invalid_type_error: 'User feed format is required' })
        .trim()
        .min(1, 'User feed format is required'),
    glAccountFeedFormat: z
        .string({
            required_error: 'GL Account feed format is required',
            invalid_type_error: 'GL Account feed format is required',
        })
        .trim()
        .min(1, 'GL Account feed format is required'),
    poValidationFeedFormat: z
        .string({ required_error: 'PO feed format is required', invalid_type_error: 'PO feed format is required' })
        .trim()
        .min(1, 'PO feed format is required'),
    poDeliveryFeedFormat: z
        .string({ required_error: 'PO feed format is required', invalid_type_error: 'PO feed format is required' })
        .trim()
        .min(1, 'PO feed format is required'),
});

const OutboundDataFeedsSchema = z.object({
    deliveryMethod: z
        .object({
            useSftp: z.boolean().default(true),
            useEmail: z.boolean().default(false),
            useOracleSupplierNetwork: z.boolean().default(false),
            useTransceptaManager: z.boolean().default(false),
            useWebservice: z.boolean().default(false),
            // allow empty fields if sftpConnection not explicitly required
            sftpConnection: z
                .object({
                    server: z.string().trim().min(1, 'Server is required').optional().or(z.literal('')),
                    user: z.string().trim().min(1, 'User is required').optional().or(z.literal('')),
                    password: z.string().trim().min(1, 'Password is required').optional().or(z.literal('')),
                    downloadFolder: z
                        .string()
                        .trim()
                        .min(1, 'Download folder is required')
                        .optional()
                        .or(z.literal('')),
                    uploadFolder: z.string().trim().min(1, 'Upload folder is required').optional().or(z.literal('')),
                    protocol: z.nativeEnum(SftpProtocol).optional().default(SftpProtocol.SFTP),
                    port: z.string().trim().min(1, 'Port is required').optional().or(z.literal('')),
                    timeoutInSeconds: z
                        .string()
                        .trim()
                        .pipe(z.coerce.number().int().positive())
                        .optional()
                        .or(z.literal('')),
                })
                .optional(),
            emailAddress: z.string().trim().email('Invalid email address').optional().or(z.literal('')),
            uri: z.string().trim().optional().or(z.literal('')),
            webserviceName: z.string().trim().optional().or(z.literal('')),
        })
        .superRefine(
            (
                {
                    useSftp,
                    useEmail,
                    useOracleSupplierNetwork,
                    useTransceptaManager,
                    useWebservice,
                    sftpConnection,
                    emailAddress,
                    uri,
                    webserviceName,
                },
                ctx
            ) => {
                if (!useSftp && !useEmail && !useOracleSupplierNetwork && !useTransceptaManager && !useWebservice) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'At least one delivery method is required',
                    });
                }
                // SFTP requires connection info
                if (useSftp) {
                    if (!sftpConnection?.user) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'User is required',
                            path: ['sftpConnection', 'user'],
                        });
                    }
                    if (!sftpConnection?.password) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Password is required',
                            path: ['sftpConnection', 'password'],
                        });
                    }
                    if (!sftpConnection?.server) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Server is required',
                            path: ['sftpConnection', 'server'],
                        });
                    }
                    if (!sftpConnection?.downloadFolder) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Download folder is required',
                            path: ['sftpConnection', 'downloadFolder'],
                        });
                    }
                    if (!sftpConnection?.uploadFolder) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Upload folder is required',
                            path: ['sftpConnection', 'uploadFolder'],
                        });
                    }
                    if (!sftpConnection?.protocol) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Protocol is required',
                            path: ['sftpConnection', 'protocol'],
                        });
                    }
                    if (!sftpConnection?.port) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Port is required',
                            path: ['sftpConnection', 'port'],
                        });
                    }
                    if (!sftpConnection?.timeoutInSeconds) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Timeout in seconds is required',
                            path: ['sftpConnection', 'timeoutInSeconds'],
                        });
                    }
                }
                // email requires email address
                if (useEmail) {
                    if (!emailAddress) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Email address is required',
                            path: ['', 'emailAddress'],
                        });
                    }
                }
                // webservice requires webservice name
                if (useWebservice) {
                    if (!webserviceName) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Webservice name is required',
                            path: ['webserviceName'],
                        });
                    }
                }
                // Oracle requires URI
                if (useOracleSupplierNetwork) {
                    if (!uri) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'URI is required',
                            path: ['uri'],
                        });
                    }
                }
            }
        ),
    fileTypes: z
        .object({
            usePDF: z.boolean().default(false).optional(),
            useTIFF: z.boolean().default(false).optional(),
            useXML: z.boolean().default(false).optional(),
            useXML1: z.boolean().default(false).optional(),
            useOther: z.boolean().default(false).optional(),
            fileTypeExtension: z.string().trim().min(1, 'File type extension is required').optional().or(z.literal('')),
        })
        .superRefine(({ usePDF, useTIFF, useXML, useXML1, useOther, fileTypeExtension }, ctx) => {
            if (!usePDF && !useTIFF && !useXML && !useXML1 && !useOther) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'At least one file type is required',
                    path: [''],
                });
            }
            if (useOther && !fileTypeExtension) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'File type extension is required',
                    path: ['fileTypeExtension'],
                });
            }
        }),
});

const BuyerSettings = z.object({
    nextGenSupplier: z
        .object({
            ocr: z.boolean().default(true),
            ideTemplateMatching: z.boolean().default(true),
            threshold: z.string().trim().optional(),
        })
        .superRefine(({ ideTemplateMatching, threshold }, ctx) => {
            if (
                ideTemplateMatching &&
                (!threshold || Number(threshold) < IDE_THRESHOLD_MIN || Number(threshold) > IDE_THRESHOLD_MAX)
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `IDE template matching threshold must be between ${IDE_THRESHOLD_MIN} and ${IDE_THRESHOLD_MAX}`,
                    path: ['threshold'],
                });
            }
        }),
    invoices: z.object({
        enable: z.boolean().default(true),
        poFlip: z.boolean().default(true),
        poCloudMatching: z.boolean().default(true),
        poCloudMatchingForceOneLine: z.boolean().default(true),
    }),
    poForDelivery: z.object({
        enable: z.boolean().default(true),
    }),
    poa: z.object({
        enable: z.boolean().default(true),
    }),
    asn: z.object({
        enable: z.boolean().default(true),
    }),
    workflow: z.object({
        parkingLot: z.boolean().default(false),
        referencePO: z.boolean().default(true),
        referenceNonPO: z.boolean().default(true),
        cloudCode: z.boolean().default(true),
    }),
    sim: z.object({
        enable: z.boolean().default(true),
    }),
});

export const BuyerSignupFormSchema = z.object({
    generalInformation: GeneralInformationSchema,
    inboundDataFeeds: InboundDataFeedsSchema,
    outboundDataFeeds: OutboundDataFeedsSchema,
    buyerSettings: BuyerSettings,
});
